import { useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiWrite, list } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { number, thaiDateTime } from '../lib/format'

/**
 * Real-time anomaly alerting and escalation (part 106). The server computes every number on this
 * page: the rolling z-score or rate of change is stored on the alert at fire time, together with
 * the observed count, the baseline mean and the standard deviation. The page never re-derives a
 * score, so what an analyst reads here is exactly what was recorded when the alert fired.
 *
 * Dedupe is visible rather than hidden: when the same rule fires inside its suppression window,
 * the evaluation response says "รวมกับรายการเดิม" and the occurrence count on the existing alert
 * advances. One condition produces one alert, not a page storm.
 */

type Severity = 'low' | 'medium' | 'high' | 'critical'
type Status = 'open' | 'acknowledged' | 'escalated' | 'resolved'
type Metric = 'audit_events' | 'findings'
type Method = 'zscore' | 'rate_of_change'

interface RuleView {
  id: string
  name: string
  metric: Metric
  metric_label: string
  method: Method
  method_label: string
  window_minutes: number
  baseline_windows: number
  threshold: number
  threshold_label: string
  min_severity: Severity
  min_severity_label: string
  dedupe_minutes: number
  min_observed: number
  enabled: boolean
  escalation_policy_id?: string
  escalation_policy_name?: string
  window_label: string
  created_at: string
  updated_at: string
}

interface RotationMember {
  level: number
  user_id: string
  name: string
}

interface PolicyView {
  id: string
  name: string
  l1_after_minutes: number
  l2_after_minutes: number
  l3_after_minutes: number
  rotation: RotationMember[]
  enabled: boolean
  ladder_label: string
}

interface AlertView {
  id: string
  rule_id: string
  rule_name: string
  metric: Metric
  method: Method
  severity: Severity
  severity_label: string
  status: Status
  status_label: string
  escalation_level: number
  level_label: string
  observed: number
  expected: number
  stddev: number
  z_score: number
  rate_of_change: number
  threshold: number
  window_start: string
  window_end: string
  dedupe_key: string
  occurrences: number
  summary: string
  on_call: string
  acknowledged_by?: string
  acknowledged_note?: string
  acknowledged_at?: string
  escalated_at?: string
  escalated_by?: string
  escalation_reason?: string
  age_minutes: number
  metric_label: string
  method_label: string
  last_seen_at: string
  created_at: string
}

interface Evaluation {
  observed: number
  expected: number
  stddev: number
  z_score: number
  rate_of_change: number
  threshold: number
  triggered: boolean
  severity: Severity | ''
  severity_label: string
  reason: string
  reason_label: string
  window_start: string
  window_end: string
  baseline_start: string
  baseline_windows: number
}

interface EvaluateResponse {
  rule: RuleView
  evaluation: Evaluation
  alert?: AlertView
  created: boolean
  suppressed: boolean
  occurrences: number
  escalated: number
  checked_at: string
  window_end: string
}

interface RuleInput {
  name: string
  metric: Metric
  method: Method
  window_minutes: number
  baseline_windows: number
  threshold: number
  min_severity: Severity
  dedupe_minutes: number
  min_observed: number
  enabled: boolean
  escalation_policy_id: string
}

interface PolicyInput {
  name: string
  l1_after_minutes: number
  l2_after_minutes: number
  l3_after_minutes: number
  rotation: RotationMember[]
  enabled: boolean
}

const severityTone: Record<Severity, string> = {
  low: 'sev-low',
  medium: 'sev-medium',
  high: 'sev-high',
  critical: 'sev-critical',
}

const statusTone: Record<Status, string> = {
  open: 'sev-high',
  acknowledged: 'sev-low',
  escalated: 'sev-critical',
  resolved: 'sev-low',
}

const severityText: Record<Severity, string> = {
  low: 'ต่ำ',
  medium: 'ปานกลาง',
  high: 'สูง',
  critical: 'วิกฤต',
}

const methodText: Record<Method, string> = {
  zscore: 'z-score',
  rate_of_change: 'อัตราการเปลี่ยนแปลง',
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function ageText(minutes: number): string {
  if (minutes < 60) return `${minutes} นาที`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest === 0 ? `${hours} ชั่วโมง` : `${hours} ชั่วโมง ${rest} นาที`
}

function rotationToList(text: string, level: number): RotationMember[] {
  return text
    .split(',')
    .map((name) => name.trim())
    .filter((name) => name.length > 0)
    .map((name) => ({ level, user_id: '', name }))
}

function rotationText(policy: PolicyView | undefined, level: number): string {
  if (!policy) return ''
  return policy.rotation.filter((member) => member.level === level).map((member) => member.name || member.user_id).join(', ')
}

export function AnomalyAlerts() {
  const qc = useQueryClient()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [ackNote, setAckNote] = useState('')
  const [escalateReason, setEscalateReason] = useState('ยกระดับเพื่อให้หัวหน้ากะตรวจสอบ')

  const [ruleForm, setRuleForm] = useState({
    name: 'ปริมาณเหตุการณ์ตรวจสอบพุ่งสูง',
    metric: 'audit_events' as Metric,
    method: 'zscore' as Method,
    window_minutes: '15',
    baseline_windows: '24',
    threshold: '4',
    dedupe_minutes: '60',
    min_severity: 'medium' as Severity,
    policy_id: '',
  })

  const [policyForm, setPolicyForm] = useState({
    name: 'สายปฏิบัติการตรวจสอบ',
    l2_after_minutes: '15',
    l3_after_minutes: '60',
    l1: 'สมชาย',
    l2: 'อารยา, ปวีณา',
    l3: 'ผู้จัดการตรวจสอบ',
  })

  const rules = useQuery({
    queryKey: ['anomaly-rules'],
    queryFn: () => list<RuleView>('/v1/anomaly/rules?include_disabled=true'),
    refetchInterval: 30_000,
  })
  const alerts = useQuery({
    queryKey: ['anomaly-alerts'],
    queryFn: () => list<AlertView>('/v1/anomaly/alerts?limit=200'),
    refetchInterval: 15_000,
  })
  const policies = useQuery({
    queryKey: ['anomaly-policies'],
    queryFn: () => list<PolicyView>('/v1/anomaly/policies'),
    refetchInterval: 60_000,
  })

  const ruleRows = rules.data ?? []
  const alertRows = alerts.data ?? []
  const policyRows = policies.data ?? []
  const active: AlertView | undefined = alertRows.find((row) => row.id === selectedId) ?? alertRows[0]

  const refreshAlerts = () => {
    void qc.invalidateQueries({ queryKey: ['anomaly-alerts'] })
    void qc.invalidateQueries({ queryKey: ['anomaly-rules'] })
  }

  const evaluate = useMutation({
    mutationFn: (rule: RuleView) => apiWrite<EvaluateResponse>(`/v1/anomaly/rules/${rule.id}/evaluate`, 'POST', {}),
    onSuccess: (result) => {
      setActionError(null)
      const ev = result.evaluation
      const head = ev.triggered
        ? `พบความผิดปกติ: ${result.rule.name} · ${ev.severity_label} · z = ${ev.z_score.toFixed(2)} · อัตราการเปลี่ยนแปลง ${(ev.rate_of_change * 100).toFixed(1)}%`
        : `ไม่พบความผิดปกติ: ${result.rule.name} · ${ev.reason_label} · z = ${ev.z_score.toFixed(2)}`
      const dedupe = result.suppressed && result.alert
        ? ` · รวมกับการแจ้งเตือนเดิม (พบซ้ำ ${number(result.occurrences)} ครั้ง)`
        : result.created && result.alert
          ? ' · เปิดการแจ้งเตือนใหม่'
          : ''
      const sweep = result.escalated > 0 ? ` · ยกระดับตามอายุ ${number(result.escalated)} รายการ` : ''
      setNotice(head + dedupe + sweep)
      refreshAlerts()
    },
    onError: (err) => {
      setNotice(null)
      setActionError(errorText(err, 'ประเมินกฎไม่สำเร็จ'))
    },
  })

  const acknowledge = useMutation({
    mutationFn: (alert: AlertView) =>
      apiWrite<{ alert: AlertView; updated: boolean }>(`/v1/anomaly/alerts/${alert.id}/ack`, 'POST', { note: ackNote }),
    onSuccess: () => {
      setActionError(null)
      setAckNote('')
      setNotice('รับทราบการแจ้งเตือนแล้ว')
      refreshAlerts()
    },
    onError: (err) => setActionError(errorText(err, 'รับทราบการแจ้งเตือนไม่สำเร็จ')),
  })

  const escalate = useMutation({
    mutationFn: (alert: AlertView) =>
      apiWrite<{ alert: AlertView; moved: boolean; reason: string }>(
        `/v1/anomaly/alerts/${alert.id}/escalate`,
        'POST',
        { reason: escalateReason },
      ),
    onSuccess: (result) => {
      setActionError(null)
      setNotice(result.moved
        ? `ยกระดับเป็น ${result.alert.level_label} · ผู้รับผิดชอบ ${result.alert.on_call || 'ไม่ระบุ'}`
        : 'การแจ้งเตือนนี้อยู่ที่ระดับสูงสุดแล้ว')
      refreshAlerts()
    },
    onError: (err) => setActionError(errorText(err, 'ยกระดับการแจ้งเตือนไม่สำเร็จ')),
  })

  const createRule = useMutation({
    mutationFn: (input: RuleInput) => apiWrite<RuleView>('/v1/anomaly/rules', 'POST', input),
    onSuccess: (rule) => {
      setActionError(null)
      setNotice(`สร้างกฎแล้ว: ${rule.name}`)
      void qc.invalidateQueries({ queryKey: ['anomaly-rules'] })
    },
    onError: (err) => setActionError(errorText(err, 'สร้างกฎไม่สำเร็จ')),
  })

  const createPolicy = useMutation({
    mutationFn: (input: PolicyInput) => apiWrite<PolicyView>('/v1/anomaly/policies', 'POST', input),
    onSuccess: (policy) => {
      setActionError(null)
      setNotice(`สร้างนโยบายยกระดับแล้ว: ${policy.name}`)
      void qc.invalidateQueries({ queryKey: ['anomaly-policies'] })
    },
    onError: (err) => setActionError(errorText(err, 'สร้างนโยบายยกระดับไม่สำเร็จ')),
  })

  const openAlerts = alertRows.filter((row) => row.status !== 'resolved')
  const criticalAlerts = openAlerts.filter((row) => row.severity === 'critical')
  const today = new Date().toDateString()
  const todayAlerts = alertRows.filter((row) => new Date(row.created_at).toDateString() === today)
  const enabledRules = ruleRows.filter((row) => row.enabled)

  const submitRule = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    createRule.mutate({
      name: ruleForm.name,
      metric: ruleForm.metric,
      method: ruleForm.method,
      window_minutes: Number(ruleForm.window_minutes),
      baseline_windows: Number(ruleForm.baseline_windows),
      threshold: Number(ruleForm.threshold),
      min_severity: ruleForm.min_severity,
      dedupe_minutes: Number(ruleForm.dedupe_minutes),
      min_observed: 0,
      enabled: true,
      escalation_policy_id: ruleForm.policy_id,
    })
  }

  const submitPolicy = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    createPolicy.mutate({
      name: policyForm.name,
      l1_after_minutes: 0,
      l2_after_minutes: Number(policyForm.l2_after_minutes),
      l3_after_minutes: Number(policyForm.l3_after_minutes),
      rotation: [
        ...rotationToList(policyForm.l1, 1),
        ...rotationToList(policyForm.l2, 2),
        ...rotationToList(policyForm.l3, 3),
      ],
      enabled: true,
    })
  }

  return (
    <section className="page">
      <PageHead
        title="การแจ้งเตือนความผิดปกติ"
        sub="ตรวจปริมาณเหตุการณ์ตรวจสอบและข้อค้นพบแบบเรียลไทม์ด้วย z-score แบบ rolling และอัตราการเปลี่ยนแปลง พร้อมลดการแจ้งเตือนซ้ำและยกระดับตามอายุ"
        actions={
          <button
            className="btn btn-primary btn-sm"
            onClick={() => {
              const first = enabledRules[0]
              if (first) {
                evaluate.mutate(first)
              } else {
                setActionError('ยังไม่มีกฎที่เปิดใช้งาน')
              }
            }}
            disabled={evaluate.isPending || enabledRules.length === 0}
          >
            {evaluate.isPending ? 'กำลังประเมิน...' : 'ประเมินกฎแรกเดี๋ยวนี้'}
          </button>
        }
      />

      <div className="grid-4 stagger">
        <Stat label="การแจ้งเตือนที่ยังเปิด" value={number(openAlerts.length)} unit="รายการ" foot="ยังไม่ปิดหรือแก้ไข" />
        <Stat label="ระดับวิกฤต" value={number(criticalAlerts.length)} unit="รายการ" foot="ต้องได้รับการตอบสนองก่อน" />
        <Stat label="แจ้งเตือนวันนี้" value={number(todayAlerts.length)} unit="รายการ" foot="ตามเวลาเซิร์ฟเวอร์" />
        <Stat label="กฎที่ใช้งาน" value={number(enabledRules.length)} unit="กฎ" foot={`ทั้งหมด ${number(ruleRows.length)} กฎ`} />
      </div>

      {notice ? (
        <div
          role="status"
          style={{
            marginTop: 16, padding: '10px 14px', fontSize: 12.5,
            border: '1px solid var(--color-emerald-soft)', borderRadius: 6,
            color: 'var(--color-emerald-soft)',
          }}
        >
          {notice}
        </div>
      ) : null}
      {actionError ? <div className="form-error" style={{ marginTop: 16 }}>{actionError}</div> : null}

      {alerts.isLoading || rules.isLoading ? (
        <div className="stack" style={{ marginTop: 16 }}><Skel h={220} /><Skel h={160} /></div>
      ) : alerts.isError ? (
        <div className="card card-pad" style={{ marginTop: 16 }}>
          <div className="form-error">{errorText(alerts.error, 'โหลดการแจ้งเตือนไม่สำเร็จ')}</div>
        </div>
      ) : alertRows.length === 0 ? (
        <div className="card card-pad" style={{ marginTop: 16 }}>
          <Empty
            title="ยังไม่มีการแจ้งเตือน"
            body="กฎจะตรวจปริมาณเหตุการณ์ตามหน้าต่างที่กำหนด เมื่อปริมาณเกินเกณฑ์จะเปิดการแจ้งเตือนพร้อมค่า z-score ที่คำนวณได้ และระบบจะรวมการแจ้งเตือนซ้ำภายในช่วง dedupe ให้เหลือรายการเดียว"
          />
        </div>
      ) : (
        <div className="grid-main" style={{ marginTop: 16 }}>
          <div className="card card-pad">
            <div className="row" style={{ alignItems: 'baseline' }}>
              <span className="card-label">คิวการแจ้งเตือน</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                เรียงใหม่สุดก่อน · เลือกแถวเพื่อดูหลักฐานและดำเนินการ
              </span>
            </div>
            <div className="table-wrap" style={{ marginTop: 12 }}>
              <table className="table data">
                <thead>
                  <tr>
                    <th>ความรุนแรง</th>
                    <th>กฎ</th>
                    <th style={{ textAlign: 'right' }}>ค่าที่พบ / คาดหมาย</th>
                    <th style={{ textAlign: 'right' }}>z</th>
                    <th>สถานะ</th>
                    <th style={{ textAlign: 'right' }}>อายุ</th>
                  </tr>
                </thead>
                <tbody>
                  {alertRows.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => setSelectedId(row.id)}
                      style={{
                        cursor: 'pointer',
                        background: row.id === active?.id ? 'rgba(200, 169, 106, 0.06)' : undefined,
                      }}
                    >
                      <td>
                        <span className={`sev ${severityTone[row.severity]}`}>
                          <i className="sev-dot" />
                          {severityText[row.severity]}
                        </span>
                      </td>
                      <td>
                        <div>{row.rule_name}</div>
                        <div className="dim" style={{ fontSize: 10.5 }}>
                          {row.metric_label} · {methodText[row.method]}
                        </div>
                      </td>
                      <td className="tnum" style={{ textAlign: 'right' }}>
                        {number(row.observed)} / {row.expected.toFixed(1)}
                        <div className="dim" style={{ fontSize: 10.5 }}>SD {row.stddev.toFixed(2)}</div>
                      </td>
                      <td className="tnum" style={{ textAlign: 'right' }}>{row.z_score.toFixed(2)}</td>
                      <td>
                        <span className={`sev ${statusTone[row.status]}`}>
                          <i className="sev-dot" />
                          {row.status_label}
                        </span>
                        <div className="dim" style={{ fontSize: 10.5 }}>{row.level_label}</div>
                      </td>
                      <td className="dim tnum" style={{ textAlign: 'right', fontSize: 11 }}>
                        {ageText(row.age_minutes)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="dim" style={{ fontSize: 11, marginTop: 10 }}>
              dedupe ทำงานที่ฐานข้อมูลด้วยคีย์ (กฎ, รอบเวลา): การประเมินซ้ำในช่วงเดียวกันจะไม่สร้างรายการใหม่
              แต่จะเพิ่มจำนวนครั้งที่พบในรายการเดิม
            </div>
          </div>

          <div className="card card-pad">
            {!active ? (
              <Empty title="ยังไม่ได้เลือกการแจ้งเตือน" body="เลือกแถวจากคิวเพื่อดูหลักฐาน" />
            ) : (
              <>
                <div className="row" style={{ alignItems: 'baseline' }}>
                  <span className="card-label">หลักฐานการตรวจจับ</span>
                  <span className={`sev ${severityTone[active.severity]}`} style={{ marginLeft: 'auto' }}>
                    <i className="sev-dot" />
                    {active.severity_label}
                  </span>
                </div>
                <div style={{ fontSize: 13, marginTop: 10 }}>{active.summary}</div>
                <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>
                  {active.rule_name} · {active.metric_label} · {methodText[active.method]} · {active.status_label} · {active.level_label}
                </div>

                <div className="grid-2" style={{ marginTop: 16 }}>
                  <div>
                    <div className="dim" style={{ fontSize: 11 }}>ค่าที่พบ</div>
                    <div className="tnum" style={{ fontSize: 24 }}>{number(active.observed)}</div>
                  </div>
                  <div>
                    <div className="dim" style={{ fontSize: 11 }}>ค่าคาดหมาย</div>
                    <div className="tnum" style={{ fontSize: 24 }}>{active.expected.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="dim" style={{ fontSize: 11 }}>z-score</div>
                    <div className="tnum" style={{ fontSize: 24 }}>{active.z_score.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="dim" style={{ fontSize: 11 }}>อัตราการเปลี่ยนแปลง</div>
                    <div className="tnum" style={{ fontSize: 24 }}>{(active.rate_of_change * 100).toFixed(1)}%</div>
                  </div>
                </div>

                <div className="stack" style={{ marginTop: 16, gap: 6, fontSize: 11.5 }}>
                  <div className="row"><span className="dim">ส่วนเบี่ยงเบนมาตรฐานฐาน</span><span className="tnum" style={{ marginLeft: 'auto' }}>{active.stddev.toFixed(3)}</span></div>
                  <div className="row"><span className="dim">เกณฑ์ที่ตั้งไว้</span><span className="tnum" style={{ marginLeft: 'auto' }}>{active.threshold.toFixed(2)}</span></div>
                  <div className="row"><span className="dim">หน้าต่างที่ตรวจ</span><span style={{ marginLeft: 'auto' }}>{thaiDateTime(active.window_start)} ถึง {thaiDateTime(active.window_end)}</span></div>
                  <div className="row"><span className="dim">พบซ้ำในช่วง dedupe</span><span className="tnum" style={{ marginLeft: 'auto' }}>{number(active.occurrences)} ครั้ง</span></div>
                  <div className="row"><span className="dim">ผู้รับผิดชอบปัจจุบัน</span><span style={{ marginLeft: 'auto' }}>{active.on_call || 'ไม่ระบุ'}</span></div>
                  <div className="row"><span className="dim">เปิดเมื่อ</span><span style={{ marginLeft: 'auto' }}>{thaiDateTime(active.created_at)}</span></div>
                  {active.acknowledged_at ? (
                    <div className="row"><span className="dim">รับทราบโดย</span><span style={{ marginLeft: 'auto' }}>{active.acknowledged_by} · {thaiDateTime(active.acknowledged_at)}</span></div>
                  ) : null}
                  {active.escalated_at ? (
                    <div className="row"><span className="dim">ยกระดับล่าสุด</span><span style={{ marginLeft: 'auto' }}>{thaiDateTime(active.escalated_at)}</span></div>
                  ) : null}
                </div>

                <div className="field">
                  <label htmlFor="anomaly-ack-note">หมายเหตุการรับทราบ</label>
                  <input
                    id="anomaly-ack-note"
                    className="input"
                    value={ackNote}
                    onChange={(event) => setAckNote(event.target.value)}
                    placeholder="เช่น กำลังตรวจสอบกับทีมปฏิบัติการ"
                  />
                </div>
                <div className="row" style={{ marginTop: 10 }}>
                  <button
                    className="btn btn-sm"
                    disabled={acknowledge.isPending || Boolean(active.acknowledged_at)}
                    onClick={() => acknowledge.mutate(active)}
                  >
                    {active.acknowledged_at ? 'รับทราบแล้ว' : acknowledge.isPending ? 'กำลังบันทึก...' : 'รับทราบ'}
                  </button>
                </div>

                <div className="field">
                  <label htmlFor="anomaly-escalate-reason">เหตุผลการยกระดับ</label>
                  <input
                    id="anomaly-escalate-reason"
                    className="input"
                    value={escalateReason}
                    onChange={(event) => setEscalateReason(event.target.value)}
                  />
                </div>
                <div className="row" style={{ marginTop: 10 }}>
                  <button
                    className="btn btn-sm"
                    disabled={escalate.isPending || active.escalation_level >= 3}
                    onClick={() => escalate.mutate(active)}
                  >
                    {active.escalation_level >= 3 ? 'ถึงระดับสูงสุดแล้ว' : escalate.isPending ? 'กำลังยกระดับ...' : 'ยกระดับหนึ่งขั้น'}
                  </button>
                  <span className="dim" style={{ fontSize: 11 }}>
                    การยกระดับตามอายุทำอัตโนมัติเมื่อประเมินกฎ
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="card card-pad" style={{ marginTop: 16 }}>
        <div className="row" style={{ alignItems: 'baseline' }}>
          <span className="card-label">กฎตรวจจับ</span>
          <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
            ประเมินด้วยข้อมูลจริงจากเหตุการณ์ตรวจสอบหรือข้อค้นพบ
          </span>
        </div>
        {ruleRows.length === 0 ? (
          <Empty title="ยังไม่มีกฎ" body="สร้างกฎแรกด้านล่างเพื่อเริ่มตรวจปริมาณเหตุการณ์" />
        ) : (
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="table data">
              <thead>
                <tr>
                  <th>ชื่อกฎ</th>
                  <th>แหล่งข้อมูล</th>
                  <th>ตัวตรวจ</th>
                  <th style={{ textAlign: 'right' }}>หน้าต่าง</th>
                  <th>เกณฑ์</th>
                  <th>นโยบายยกระดับ</th>
                  <th>สถานะ</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {ruleRows.map((rule) => (
                  <tr key={rule.id}>
                    <td>{rule.name}</td>
                    <td className="dim">{rule.metric_label}</td>
                    <td className="dim">{rule.method_label}</td>
                    <td className="dim tnum" style={{ textAlign: 'right' }}>{rule.window_label}</td>
                    <td className="tnum">{rule.threshold_label}</td>
                    <td className="dim">{rule.escalation_policy_name || 'ไม่มี'}</td>
                    <td>
                      <span className={`sev ${rule.enabled ? 'sev-low' : 'sev-medium'}`}>
                        <i className="sev-dot" />
                        {rule.enabled ? 'ใช้งาน' : 'ปิด'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        className="btn btn-sm"
                        disabled={evaluate.isPending || !rule.enabled}
                        onClick={() => evaluate.mutate(rule)}
                      >
                        ประเมินเดี๋ยวนี้
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid-2" style={{ marginTop: 16 }}>
        <div className="card card-pad">
          <span className="card-label">สร้างกฎใหม่</span>
          <form onSubmit={submitRule}>
            <div className="field">
              <label htmlFor="anomaly-rule-name">ชื่อกฎ</label>
              <input
                id="anomaly-rule-name"
                className="input"
                value={ruleForm.name}
                onChange={(event) => setRuleForm({ ...ruleForm, name: event.target.value })}
                required
              />
            </div>
            <div className="grid-2">
              <div className="field">
                <label htmlFor="anomaly-rule-metric">แหล่งข้อมูล</label>
                <select
                  id="anomaly-rule-metric"
                  className="select"
                  value={ruleForm.metric}
                  onChange={(event) => setRuleForm({ ...ruleForm, metric: event.target.value as Metric })}
                >
                  <option value="audit_events">เหตุการณ์ตรวจสอบ</option>
                  <option value="findings">ข้อค้นพบ</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="anomaly-rule-method">ตัวตรวจ</label>
                <select
                  id="anomaly-rule-method"
                  className="select"
                  value={ruleForm.method}
                  onChange={(event) => {
                    const method = event.target.value as Method
                    setRuleForm({ ...ruleForm, method, threshold: method === 'zscore' ? '4' : '1' })
                  }}
                >
                  <option value="zscore">z-score แบบ rolling</option>
                  <option value="rate_of_change">อัตราการเปลี่ยนแปลง</option>
                </select>
              </div>
            </div>
            <div className="grid-2">
              <div className="field">
                <label htmlFor="anomaly-rule-window">ความกว้างหน้าต่าง (นาที)</label>
                <input
                  id="anomaly-rule-window"
                  className="input"
                  type="number"
                  min={5}
                  max={1440}
                  value={ruleForm.window_minutes}
                  onChange={(event) => setRuleForm({ ...ruleForm, window_minutes: event.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor="anomaly-rule-baseline">จำนวนช่วงฐาน</label>
                <input
                  id="anomaly-rule-baseline"
                  className="input"
                  type="number"
                  min={4}
                  max={288}
                  value={ruleForm.baseline_windows}
                  onChange={(event) => setRuleForm({ ...ruleForm, baseline_windows: event.target.value })}
                />
              </div>
            </div>
            <div className="grid-2">
              <div className="field">
                <label htmlFor="anomaly-rule-threshold">เกณฑ์ (z หรือสัดส่วน)</label>
                <input
                  id="anomaly-rule-threshold"
                  className="input"
                  type="number"
                  step="0.01"
                  min={0.01}
                  value={ruleForm.threshold}
                  onChange={(event) => setRuleForm({ ...ruleForm, threshold: event.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor="anomaly-rule-dedupe">ช่วง dedupe (นาที)</label>
                <input
                  id="anomaly-rule-dedupe"
                  className="input"
                  type="number"
                  min={5}
                  max={10080}
                  value={ruleForm.dedupe_minutes}
                  onChange={(event) => setRuleForm({ ...ruleForm, dedupe_minutes: event.target.value })}
                />
              </div>
            </div>
            <div className="grid-2">
              <div className="field">
                <label htmlFor="anomaly-rule-severity">ระดับต่ำสุด</label>
                <select
                  id="anomaly-rule-severity"
                  className="select"
                  value={ruleForm.min_severity}
                  onChange={(event) => setRuleForm({ ...ruleForm, min_severity: event.target.value as Severity })}
                >
                  <option value="low">ต่ำ</option>
                  <option value="medium">ปานกลาง</option>
                  <option value="high">สูง</option>
                  <option value="critical">วิกฤต</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="anomaly-rule-policy">นโยบายยกระดับ</label>
                <select
                  id="anomaly-rule-policy"
                  className="select"
                  value={ruleForm.policy_id}
                  onChange={(event) => setRuleForm({ ...ruleForm, policy_id: event.target.value })}
                >
                  <option value="">ไม่มี</option>
                  {policyRows.map((policy) => (
                    <option key={policy.id} value={policy.id}>{policy.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="row" style={{ marginTop: 12 }}>
              <button className="btn btn-primary btn-sm" type="submit" disabled={createRule.isPending}>
                {createRule.isPending ? 'กำลังสร้าง...' : 'สร้างกฎ'}
              </button>
            </div>
          </form>
        </div>

        <div className="card card-pad">
          <span className="card-label">นโยบายยกระดับและเวรรับผิดชอบ</span>
          <div className="stack" style={{ marginTop: 10, gap: 10 }}>
            {policyRows.length === 0 ? (
              <div className="dim" style={{ fontSize: 12 }}>ยังไม่มีนโยบายยกระดับ</div>
            ) : (
              policyRows.map((policy) => (
                <div key={policy.id} style={{ borderTop: '1px solid var(--color-hairline)', paddingTop: 10 }}>
                  <div className="row" style={{ alignItems: 'baseline' }}>
                    <span style={{ fontSize: 12.5 }}>{policy.name}</span>
                    <span className="dim" style={{ marginLeft: 'auto', fontSize: 11 }}>{policy.ladder_label}</span>
                  </div>
                  <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                    L1 {rotationText(policy, 1) || 'ไม่ระบุ'} · L2 {rotationText(policy, 2) || 'ไม่ระบุ'} · L3 {rotationText(policy, 3) || 'ไม่ระบุ'}
                  </div>
                </div>
              ))
            )}
          </div>

          <form onSubmit={submitPolicy} style={{ marginTop: 14 }}>
            <div className="field">
              <label htmlFor="anomaly-policy-name">ชื่อนโยบาย</label>
              <input
                id="anomaly-policy-name"
                className="input"
                value={policyForm.name}
                onChange={(event) => setPolicyForm({ ...policyForm, name: event.target.value })}
                required
              />
            </div>
            <div className="grid-2">
              <div className="field">
                <label htmlFor="anomaly-policy-l2">ยกระดับเป็น L2 หลัง (นาที)</label>
                <input
                  id="anomaly-policy-l2"
                  className="input"
                  type="number"
                  min={1}
                  value={policyForm.l2_after_minutes}
                  onChange={(event) => setPolicyForm({ ...policyForm, l2_after_minutes: event.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor="anomaly-policy-l3">ยกระดับเป็น L3 หลัง (นาที)</label>
                <input
                  id="anomaly-policy-l3"
                  className="input"
                  type="number"
                  min={2}
                  value={policyForm.l3_after_minutes}
                  onChange={(event) => setPolicyForm({ ...policyForm, l3_after_minutes: event.target.value })}
                />
              </div>
            </div>
            <div className="field">
              <label htmlFor="anomaly-policy-l1-names">เวร L1 (คั่นด้วยจุลภาค)</label>
              <input
                id="anomaly-policy-l1-names"
                className="input"
                value={policyForm.l1}
                onChange={(event) => setPolicyForm({ ...policyForm, l1: event.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="anomaly-policy-l2-names">เวร L2 (คั่นด้วยจุลภาค)</label>
              <input
                id="anomaly-policy-l2-names"
                className="input"
                value={policyForm.l2}
                onChange={(event) => setPolicyForm({ ...policyForm, l2: event.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="anomaly-policy-l3-names">เวร L3 (คั่นด้วยจุลภาค)</label>
              <input
                id="anomaly-policy-l3-names"
                className="input"
                value={policyForm.l3}
                onChange={(event) => setPolicyForm({ ...policyForm, l3: event.target.value })}
              />
            </div>
            <div className="row" style={{ marginTop: 12 }}>
              <button className="btn btn-primary btn-sm" type="submit" disabled={createPolicy.isPending}>
                {createPolicy.isPending ? 'กำลังสร้าง...' : 'สร้างนโยบาย'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  )
}
